import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { LeadService } from 'src/app/private/services/lead.service';
import { EmailHubService } from 'src/app/private/services/email-hub.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Customer360Service } from 'src/app/private/services/customer-360.service';
import { activityTypeLabel } from 'src/app/utils/customer-360-labels.util';
import { MODULE_LOADING_MESSAGES } from 'src/app/private/services/module.config';
import { CustomerIntelligenceResponse, CustomerIntelligenceOffer, CustomerIntelligenceRfmDetail } from 'src/app/models/customer-intelligence.model';
import {
  ActivityBreakdownEntry,
  intelInfo,
  intelGraphVisual,
  IntelGraphVisual,
  intelValue,
  parseActivityBreakdown,
  toAgentInsightText,
  toAgentActionText,
} from 'src/app/utils/customer-intelligence.util';
import { LeadActivityService } from 'src/app/private/services/lead-activity.service';
import { buildCommunicationLogPayload } from 'src/app/utils/communication-log.util';
import {
  getApiErrorMessage,
  isMailboxLinkRequiredMessage,
  showMailboxLinkRequiredToast,
} from 'src/app/utils/mail-hub-response.util';

export type AnalyticsSubTab = 'overview' | 'spending' | 'behavior' | 'loyalty' | 'recommendations';

export interface SendableOffer {
  code: string;
  description: string;
  type: string;
  offer_id?: number;
}

@Component({
  selector: 'app-lead-profile-analytics',
  templateUrl: './lead-profile-analytics.component.html',
  styleUrls: ['./lead-profile-analytics.component.scss'],
})
export class LeadProfileAnalyticsComponent implements OnChanges, OnDestroy {
  @Input() leadId: number | string | null = null;
  @Input() refreshToken = 0;
  @Input() contactEmail: string | null = null;
  @Input() contactPhone: string | null = null;
  @Input() contactName: string | null = null;

  private _loading = false;
  get loading(): boolean {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    if (val) this.startMessageRotation();
    else this.cleanupMessageRotation();
  }

  analyticsActiveMessages: string[] = [];
  analyticsCurrentMessageIndex = 0;
  analyticsCurrentMessage = '';
  analyticsFadeState: 'fade-out' | 'fade-in' | '' = '';
  private analyticsMessageInterval: any = null;

  intelligence: CustomerIntelligenceResponse | null = null;
  loadError = false;
  activeSubTab: AnalyticsSubTab = 'overview';

  readonly offerEmailSubject = 'Exclusive offer just for you';
  emailBodyText = '';
  whatsappMessageText = '';
  selectedSendOfferCode = '';
  sendingOffer = false;

  @ViewChild('whatsappSendModal') whatsappSendModal!: TemplateRef<unknown>;
  @ViewChild('emailSendModal') emailSendModal!: TemplateRef<unknown>;
  private whatsappModalRef: NgbModalRef | null = null;
  private emailModalRef: NgbModalRef | null = null;

  readonly analyticsSubTabs: { id: AnalyticsSubTab; label: string; icon: string; hint: string }[] = [
    { id: 'overview', label: 'Overview', icon: 'fa-th-large', hint: 'Scores & summary' },
    { id: 'spending', label: 'Spending', icon: 'fa-line-chart', hint: 'RFM & purchases' },
    { id: 'behavior', label: 'Behavior', icon: 'fa-user', hint: 'Preferences & churn' },
    { id: 'loyalty', label: 'Loyalty', icon: 'fa-diamond', hint: 'Points & tier' },
    { id: 'recommendations', label: 'Actions', icon: 'fa-magic', hint: 'Next steps & offers' },
  ];

  constructor(
    private customer360: Customer360Service,
    private leadApi: LeadService,
    private emailHub: EmailHubService,
    private localStorage: LocalStorageService,
    private leadActivity: LeadActivityService,
    private toast: ToastrService,
    private modalService: NgbModal
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] || changes['refreshToken']) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.cleanupMessageRotation();
  }

  get layer4() {
    return this.intelligence?.layer4;
  }

  get layer5() {
    return this.intelligence?.layer5;
  }

  get churn() {
    return this.intelligence?.churn;
  }

  get activityBreakdownEntries(): ActivityBreakdownEntry[] {
    return parseActivityBreakdown(this.layer4?.activity_breakdown);
  }

  get maxBreakdownValue(): number {
    return Math.max(...this.activityBreakdownEntries.map((e) => e.value), 1);
  }

  get rfmSegment(): string {
    return String(intelValue<string>(this.layer4?.rfm_segment) ?? '');
  }

  get loyaltyTier(): string {
    return String(intelValue<string>(this.layer4?.loyalty_tier) ?? 'Standard');
  }

  get rfmDetail(): CustomerIntelligenceRfmDetail | null {
    return intelValue<CustomerIntelligenceRfmDetail>(this.layer4?.rfm_detail);
  }

  get churnRisk(): string {
    return String(intelValue<string>(this.churn?.risk) ?? '');
  }

  get churnReasons(): string[] {
    return intelValue<string[]>(this.churn?.reasons) ?? [];
  }

  get retentionUrgency(): string {
    return String(intelValue<string>(this.churn?.retention_urgency) ?? '');
  }

  get preferredLocation(): string {
    return String(intelValue<string>(this.layer4?.preferred_location) ?? '');
  }

  get preferredActivity(): string {
    return String(intelValue<string>(this.layer4?.preferred_activity) ?? '');
  }

  get preferredPayment(): string {
    return String(intelValue<string>(this.layer4?.preferred_payment) ?? '');
  }

  get avgRating(): number | null {
    return intelValue<number>(this.layer4?.avg_rating);
  }

  get totalFeedbacks(): number {
    return intelValue<number>(this.layer4?.total_feedbacks) ?? 0;
  }

  get complaintsCount(): number {
    return intelValue<number>(this.layer4?.complaints_count) ?? 0;
  }

  get spendTrendValue(): string {
    return String(intelValue<string>(this.layer4?.spend_trend) ?? 'stable');
  }

  fieldInfo(raw: unknown): string | null {
    return intelInfo(raw);
  }

  readonly graphDotIndices = [0, 1, 2, 3, 4];

  fieldGraphVisual(raw: unknown): IntelGraphVisual | null {
    return intelGraphVisual(raw);
  }

  trendVisualIcon(trend?: 'up' | 'down' | 'flat'): string {
    if (trend === 'up') return 'fa-arrow-up';
    if (trend === 'down') return 'fa-arrow-down';
    return 'fa-minus';
  }

  trendVisualLabel(trend?: 'up' | 'down' | 'flat'): string {
    if (trend === 'up') return 'Rising';
    if (trend === 'down') return 'Falling';
    return 'Stable';
  }

  get segmentDescription(): string {
    const segment = this.rfmSegment.toLowerCase().replace(/\s+/g, '_');
    const map: Record<string, string> = {
      inactive: 'Has not engaged recently — may need a win-back nudge.',
      champions: 'Highly engaged and valuable — treat as a VIP.',
      loyal: 'Returns often and spends well — nurture the relationship.',
      loyal_customer: 'Returns often and spends well — nurture the relationship.',
      'loyal customer': 'Returns often and spends well — nurture the relationship.',
      potential: 'Shows promise — a timely offer could convert them.',
      'potential loyalist': 'Shows promise — a timely offer could convert them.',
      at_risk: 'Engagement is slipping — act before they churn.',
      'at risk vip': 'High-value customer at risk — prioritise retention outreach.',
      new: 'Recently joined — focus on a great first experience.',
      'new customer': 'Recently joined — focus on a great first experience.',
      'lost customer': 'Has not returned recently — win-back campaign recommended.',
    };
    if (map[segment]) return map[segment];
    if (segment.includes('loyal')) return map['loyal'];
    if (segment.includes('champion')) return map['champions'];
    if (segment.includes('risk')) return map['at_risk'];
    return 'Profile built from recent visits, feedback, and loyalty activity.';
  }

  get churnHeadline(): string {
    const risk = this.churnRisk.toLowerCase();
    if (risk === 'high') return 'Needs attention soon';
    if (risk === 'medium') return 'Worth monitoring';
    return 'Looking healthy';
  }

  get spendTrendLabel(): string {
    const trend = this.spendTrendValue.toLowerCase();
    if (trend === 'increasing' || trend === 'up') return 'Spending more lately';
    if (trend === 'decreasing' || trend === 'down') return 'Spending less lately';
    return 'Spending is steady';
  }

  get spendTrendIcon(): string {
    const trend = this.spendTrendValue.toLowerCase();
    if (trend === 'increasing' || trend === 'up') return 'fa-long-arrow-up';
    if (trend === 'decreasing' || trend === 'down') return 'fa-long-arrow-down';
    return 'fa-arrows-h';
  }

  get preferredPaymentIcon(): string {
    const payment = (this.preferredPayment || '').toLowerCase();
    if (payment.includes('cash')) return 'fa-coins';
    if (payment.includes('upi') || payment.includes('qr')) return 'fa-qrcode';
    if (payment.includes('wallet') || payment.includes('paytm') || payment.includes('phonepe')) return 'fa-mobile';
    if (payment.includes('net') || payment.includes('bank') || payment.includes('transfer')) return 'fa-university';
    return 'fa-credit-card';
  }

  get recencyLabel(): string {
    const days = this.rfmDetail?.recency_days;
    if (days == null) return 'No recent visit recorded';
    if (days === 0) return 'Visited today';
    if (days === 1) return 'Visited yesterday';
    return `Last visit ${days} days ago`;
  }

  get primaryOffer(): { code: string; description: string; type: string } | null {
    const raw = intelValue<{ code?: string; description?: string; type?: string }>(
      this.layer5?.primary_offer
    );
    if (!raw?.code?.trim()) return null;
    return {
      code: raw.code,
      description: raw.description ?? '',
      type: raw.type ?? '',
    };
  }

  get recommendedOffers(): CustomerIntelligenceOffer[] {
    return this.intelligence?.recommended_offers ?? [];
  }

  get topPickOffer(): SendableOffer | null {
    const po = this.primaryOffer;
    if (!po) return null;
    return { code: po.code, description: po.description, type: po.type };
  }

  get sendOfferOptions(): SendableOffer[] {
    const options: SendableOffer[] = [];
    if (this.topPickOffer) options.push(this.topPickOffer);
    for (const offer of this.recommendedOffers) {
      if (!options.some((o) => o.code === offer.code)) {
        options.push({
          code: offer.code,
          description: offer.description,
          type: offer.type,
          offer_id: offer.offer_id,
        });
      }
    }
    return options;
  }

  get selectedSendOffer(): SendableOffer | null {
    return (
      this.sendOfferOptions.find((o) => o.code === this.selectedSendOfferCode) ??
      this.topPickOffer
    );
  }

  private hasValidPhone(value: string | null | undefined): boolean {
    const raw = String(value || '').trim();
    if (!raw || raw === '—' || raw === '-') return false;
    return raw.replace(/\D/g, '').length >= 8;
  }

  private hasValidEmail(value: string | null | undefined): boolean {
    const raw = String(value || '').trim();
    if (!raw || raw === '—' || raw === '-') return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
  }

  get canSendViaEmail(): boolean {
    return this.hasValidEmail(this.contactEmail);
  }

  get canSendViaWhatsapp(): boolean {
    return this.hasValidPhone(this.contactPhone);
  }

  /** Primary channel hint from intelligence when multiple contact methods exist. */
  get recommendedChannel(): 'email' | 'whatsapp' | null {
    const channel = (this.layer5?.recommended_channel || '').toLowerCase();
    if (this.canSendViaWhatsapp && this.canSendViaEmail) {
      if (channel.includes('email')) return 'email';
      if (channel.includes('whatsapp')) return 'whatsapp';
      return null;
    }
    if (this.canSendViaWhatsapp) return 'whatsapp';
    if (this.canSendViaEmail) return 'email';
    if (channel.includes('whatsapp') && this.canSendViaWhatsapp) return 'whatsapp';
    if (channel.includes('email') && this.canSendViaEmail) return 'email';
    return null;
  }

  get recommendedChannelLabel(): string {
    if (this.canSendViaEmail && this.canSendViaWhatsapp) return 'Email & WhatsApp';
    if (this.recommendedChannel === 'whatsapp') return 'WhatsApp';
    if (this.recommendedChannel === 'email') return 'Email';
    return '—';
  }

  get loyaltyInactiveLabel(): string {
    const days = intelValue<number | null>(this.layer4?.loyalty_inactive_days);
    if (days == null) return 'Active recently';
    if (days === 0) return 'Active today';
    return `${days} days since last loyalty activity`;
  }

  get activeTabHint(): string {
    return this.analyticsSubTabs.find((t) => t.id === this.activeSubTab)?.hint ?? '';
  }

  scoreRingColor(pct: number): string {
    if (pct >= 70) return '#22c55e';
    if (pct >= 40) return '#f59e0b';
    return '#ef4444';
  }

  get insightSummary(): string | null {
    return toAgentInsightText(this.layer5?.insight_summary);
  }

  get alertMessage(): string | null {
    return toAgentInsightText(this.layer5?.alert);
  }

  get nextBestAction(): string | null {
    return toAgentActionText(this.layer5?.next_best_action);
  }

  get loyaltyAction(): string | null {
    return toAgentActionText(this.layer5?.loyalty_action);
  }

  isUnknown(value: string | null | undefined): boolean {
    return !value || value.trim().toLowerCase() === 'unknown';
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null) return '—';
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }

  formatAverageSpendValue(): string {
    const value = intelValue<number>(this.layer4?.average_spend_value);
    if (value == null) return '—';
    return this.formatCurrency(value);
  }

  formatMonetary(): string {
    const monetary = this.rfmDetail?.monetary;
    if (monetary == null) return '—';
    return this.formatCurrency(Number(monetary));
  }

  formatPoints(raw: unknown): string {
    const value = intelValue<number>(raw);
    if (value == null) return '—';
    return Number(value).toLocaleString('en-IN');
  }

  formatRelevance(relevance: number | null | undefined): string | null {
    if (relevance == null) return null;
    return `${Math.round(relevance * (relevance <= 1 ? 100 : 1))}%`;
  }

  get visitCount(): string {
    const freq = this.rfmDetail?.frequency;
    return freq != null ? String(freq) : '—';
  }

  get rfmScores() {
    return this.layer4?.rfm_scores;
  }

  setSubTab(tab: AnalyticsSubTab): void {
    this.activeSubTab = tab;
  }

  tierClass(tier?: string): string {
    return (tier || this.loyaltyTier || 'standard').toLowerCase().replace(/\s+/g, '-');
  }

  load(): void {
    if (this.leadId == null || this.leadId === '') return;

    this.loading = true;
    this.loadError = false;
    this.intelligence = null;

    this.customer360.getCustomerIntelligence(this.leadId).subscribe({
      next: (res) => {
        this.intelligence = res;
        this.initOfferSendContent();
        this.loading = false;
      },
      error: () => {
        this.intelligence = null;
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  activityLabel(key: string): string {
    return activityTypeLabel(key);
  }

  offerTypeLabel(type: string): string {
    const label = activityTypeLabel(type);
    return label !== type ? label : type.replace(/_/g, ' ');
  }

  churnRiskClass(risk?: string): string {
    const level = (risk ?? this.churnRisk).toLowerCase();
    if (level === 'high') return 'is-high';
    if (level === 'medium') return 'is-medium';
    return 'is-low';
  }

  scoreLevel(pct: number): string {
    if (pct >= 70) return 'is-good';
    if (pct >= 40) return 'is-mid';
    return 'is-low';
  }

  offerIcon(type: string): string {
    const t = (type || '').toLowerCase();
    if (t.includes('winback') || t.includes('win_back')) return 'fas fa-undo-alt';
    if (t.includes('loyalty') || t.includes('point')) return 'fas fa-star';
    if (t.includes('food') || t.includes('dining') || t.includes('restaurant') || t.includes('court')) return 'fas fa-utensils';
    if (t.includes('upsell') || t.includes('upgrade')) return 'fas fa-arrow-circle-up';
    if (t.includes('ev') || t.includes('charging') || t.includes('electric')) return 'fas fa-bolt';
    if (t.includes('discount') || t.includes('percent') || t.includes('off')) return 'fas fa-percent';
    if (t.includes('gift') || t.includes('reward') || t.includes('voucher')) return 'fas fa-gift';
    if (t.includes('referral') || t.includes('refer')) return 'fas fa-user-plus';
    if (t.includes('birthday') || t.includes('anniversary')) return 'fas fa-birthday-cake';
    if (t.includes('retail') || t.includes('shop') || t.includes('store') || t.includes('purchase')) return 'fas fa-shopping-bag';
    if (t.includes('cinema') || t.includes('movie') || t.includes('entertain')) return 'fas fa-film';
    if (t.includes('travel') || t.includes('hotel') || t.includes('stay')) return 'fas fa-plane';
    return 'fas fa-ticket-alt';
  }

  initOfferSendContent(): void {
    const offer = this.topPickOffer;
    if (!offer) {
      this.emailBodyText = '';
      this.whatsappMessageText = '';
      this.selectedSendOfferCode = '';
      return;
    }
    this.selectedSendOfferCode = offer.code;
    this.emailBodyText = this.formatOfferMessage(offer);
    this.whatsappMessageText = this.formatOfferMessage(offer);
  }

  onSendOfferSelectionChange(): void {
    const offer = this.selectedSendOffer;
    if (!offer) return;
    this.emailBodyText = this.formatOfferMessage(offer);
    this.whatsappMessageText = this.formatOfferMessage(offer);
  }

  formatOfferPlainText(offer: SendableOffer): string {
    return `${offer.code}: ${offer.description}`;
  }

  formatOfferMessage(offer: SendableOffer): string {
    const name = this.contactName?.trim();
    const greeting = name && name !== 'Lead' ? `Hi ${name}!` : 'Hi there!';
    return (
      `${greeting}\n\n` +
      `We have a special offer for you:\n` +
      `${offer.code} — ${offer.description}\n\n` +
      `Redeem it on your next visit. Thank you for being a valued customer!`
    );
  }

  copyOffer(offer: SendableOffer): void {
    const text = `${offer.code} — ${offer.description}`;
    navigator.clipboard.writeText(text).then(
      () => this.toast.success('Offer copied'),
      () => this.toast.error('Could not copy offer')
    );
  }

  copyOfferText(): void {
    const offer = this.selectedSendOffer ?? this.topPickOffer;
    const text = offer ? this.formatOfferPlainText(offer) : '';
    if (!text?.trim()) {
      this.toast.warning('No offer text to copy');
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => this.toast.success('Offer copied'),
      () => this.toast.error('Could not copy offer')
    );
  }

  sendOfferByEmail(): void {
    const recipient = this.contactEmail?.trim();
    if (!recipient) {
      this.toast.error('No email address on this lead');
      return;
    }
    if (!this.emailBodyText.trim()) {
      this.toast.error('Email body is empty');
      return;
    }

    const ep = this.localStorage.getEmailProviderTypeForPayload();
    if (!['OUTLOOK', 'GMAIL', 'SES'].includes(ep)) {
      this.toast.error('Configure Outlook, Gmail, or SES in communication preferences');
      return;
    }

    this.sendingOffer = true;
    const bodyHtml = this.plainTextToHtml(this.emailBodyText);
    const displayName = this.localStorage.getProfileName() || 'Gravitas AI';
    const hubUserEmail = this.localStorage.getMailHubUserEmail();
    const sesAddr = this.localStorage.getSendEmailFromAddress();
    const fromForSes = `"${displayName}" <${sesAddr}>`;

    const onDone = (ok: boolean, msg?: string) => {
      this.sendingOffer = false;
      if (ok) {
        this.toast.success('Offer email sent');
        this.saveOfferCommunicationLog('email', this.emailBodyText, {
          subject: this.offerEmailSubject,
          to: recipient,
        });
        this.closeEmailModal();
      } else {
        if (msg && isMailboxLinkRequiredMessage(msg)) {
          showMailboxLinkRequiredToast(this.toast, msg);
        } else {
          this.toast.error(msg || 'Failed to send email');
        }
      }
    };

    if (ep === 'OUTLOOK') {
      this.emailHub
        .sendEmailUsingOutlook({
          subject: this.offerEmailSubject,
          contentType: 'HTML',
          bodyContent: bodyHtml,
          recipientEmails: [recipient],
          user_email: hubUserEmail,
          ccRecipients: [],
          attachments: [],
          client_id: this.localStorage.getClientId(),
        })
        .subscribe({
          next: () => onDone(true),
          error: (err: unknown) => {
            const message = getApiErrorMessage(err);
            onDone(
              false,
              isMailboxLinkRequiredMessage(message)
                ? message
                : message || 'Failed to send email'
            );
          },
        });
      return;
    }

    if (ep === 'GMAIL') {
      this.emailHub
        .sendEmailUsingGmail({
          client_id: this.localStorage.getClientId(),
          user_email: hubUserEmail,
          to: recipient,
          subject: this.offerEmailSubject,
          body: bodyHtml,
        })
        .subscribe({
          next: () => onDone(true),
          error: (err: unknown) => {
            const message = getApiErrorMessage(err);
            onDone(
              false,
              isMailboxLinkRequiredMessage(message)
                ? message
                : message || 'Failed to send email'
            );
          },
        });
      return;
    }

    this.leadApi
      .sendEmail({
        client_id: this.localStorage.getClientId(),
        subject: this.offerEmailSubject,
        text: '',
        html: bodyHtml,
        from_email: fromForSes,
        to: recipient,
        attachments: [],
      })
      .subscribe({
        next: () => onDone(true),
        error: () => onDone(false),
      });
  }

  openWhatsappSendModal(): void {
    if (!this.topPickOffer) return;
    this.whatsappModalRef = this.modalService.open(this.whatsappSendModal, {
      size: 'md',
      scrollable: true,
      centered: true,
      backdrop: 'static',
    });
  }

  openEmailSendModal(): void {
    if (!this.topPickOffer) return;
    this.emailModalRef = this.modalService.open(this.emailSendModal, {
      size: 'md',
      scrollable: true,
      centered: true,
      backdrop: 'static',
    });
  }

  closeWhatsappModal(): void {
    this.whatsappModalRef?.close();
    this.whatsappModalRef = null;
  }

  closeEmailModal(): void {
    this.emailModalRef?.close();
    this.emailModalRef = null;
  }

  sendOfferByWhatsapp(): void {
    const phone = this.contactPhone?.trim();
    if (!phone) {
      this.toast.error('No phone number on this lead');
      return;
    }
    if (!this.whatsappMessageText.trim()) {
      this.toast.error('WhatsApp message is empty');
      return;
    }

    this.sendingOffer = true;
    this.leadApi
      .sendWhatsappMsg({
        contacts: phone,
        client_id: String(this.localStorage.getClientId()),
        message: this.whatsappMessageText,
      })
      .subscribe({
        next: (res: { message?: string }) => {
          this.sendingOffer = false;
          this.toast.success(res?.message || 'WhatsApp message sent');
          this.saveOfferCommunicationLog('whatsapp', this.whatsappMessageText, {
            to: phone,
            message: this.whatsappMessageText,
          });
          this.closeWhatsappModal();
        },
        error: (err: { error?: { message?: string } }) => {
          this.sendingOffer = false;
          this.toast.error(
            err?.error?.message || 'Failed to send WhatsApp message',
          );
        },
      });
  }

  private saveOfferCommunicationLog(
    type: 'email' | 'whatsapp',
    note: string,
    extra: Record<string, unknown> = {}
  ): void {
    if (!this.leadId) {
      return;
    }

    const payload = buildCommunicationLogPayload(this.localStorage, {
      lead_id: this.leadId,
      communication_type: type,
      note,
      status: 'Sent',
      email_extra_details: extra,
    });

    this.leadApi.CreateCommunicationHisLog(payload).subscribe({
      next: (res: { id?: number }) => {
        if (res?.id) {
          this.leadActivity
            .recordCommunication(this.leadId!, res.id, type, note, 'Sent')
            .subscribe();
        }
      },
      error: (err) =>
        console.error('Failed to save offer communication history', err),
    });
  }

  private plainTextToHtml(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<p>${escaped.replace(/\n/g, '<br>')}</p>`;
  }

  private startMessageRotation(): void {
    if (this.analyticsMessageInterval) return;
    this.analyticsActiveMessages =
      MODULE_LOADING_MESSAGES['forecast'] ||
      MODULE_LOADING_MESSAGES['generic'] ||
      ['Loading customer analytics…'];
    this.analyticsCurrentMessageIndex = 0;
    this.analyticsCurrentMessage = this.analyticsActiveMessages[0];
    this.analyticsFadeState = '';

    this.analyticsMessageInterval = setInterval(() => {
      this.analyticsFadeState = 'fade-out';
      setTimeout(() => {
        if (!this.loading) {
          this.cleanupMessageRotation();
          return;
        }
        this.analyticsCurrentMessageIndex =
          (this.analyticsCurrentMessageIndex + 1) % this.analyticsActiveMessages.length;
        this.analyticsCurrentMessage = this.analyticsActiveMessages[this.analyticsCurrentMessageIndex];
        this.analyticsFadeState = 'fade-in';
        setTimeout(() => (this.analyticsFadeState = ''), 200);
      }, 200);
    }, 2000);
  }

  private cleanupMessageRotation(): void {
    if (this.analyticsMessageInterval) {
      clearInterval(this.analyticsMessageInterval);
      this.analyticsMessageInterval = null;
    }
  }
}
